import { useState, useEffect } from 'react'
import { LabelFaded } from '../../../ui_reusable/styles/CreateInputUtils.styles'
import {
  FormSelectPicker,
  FormDatePicker,
} from '../../../ui_reusable/StandardSelectPickers'
import {
  RadioProvider,
  StandardRadio,
} from '../../../ui_reusable/StandardRadioButtons'
import {
  CombinedRowTitle,
  DateRangeLabel,
  OrDivider,
  RadioContainer,
} from './styles/BuildModelTimeframeSelector.styles'
import { InputsWrapper, StyledInput } from './styles/ParametersForm.styles'
import { Dataset, Timeframe } from '@/api/generated/models'

type Preset = 'beginning' | '3years' | '5years' | null

const BuildModelTimeframeSelector = ({
  daterange,
  timeframe,
  dependentDriver,
  onTimeframeChange,
  error,
}: {
  daterange: Timeframe
  timeframe: Timeframe
  dependentDriver: Dataset | null
  onTimeframeChange: (value: Timeframe) => void
  error: any
}) => {
  const [selectedPreset, setSelectedPreset] = useState<Preset>(null)
  const [disabledPresets, setDisabledPresets] = useState<Preset[]>([])
  const [selectionMode, setSelectionMode] = useState<string>('manual')

  const presetOptions = [
    { label: 'Since beginning', value: 'beginning' },
    { label: 'Last 3 years', value: '3years' },
    { label: 'Last 5 years', value: '5years' },
  ]

  // Handle radio button selection change
  const handleModeChange = (value: string) => {
    setSelectionMode(value)
  }

  // Function to check which presets should be disabled based on the allowed daterange
  useEffect(() => {
    if (!daterange || !dependentDriver) {
      setDisabledPresets([])
      return
    }

    const today = new Date()
    const minDate = new Date(`${daterange.from} 00:00:00`)
    const maxDate = new Date(`${daterange.to} 23:59:00`)
    const disabled: Preset[] = []

    // "Since beginning" is always valid if we have a daterange

    // Check "Last 3 years"
    let startDate = new Date(today)
    startDate.setFullYear(startDate.getFullYear() - 3)
    let endDate = new Date(today)
    if (endDate < minDate || startDate > maxDate) {
      // The entire range is outside the allowed daterange
      disabled.push('3years')
    }

    // Check "Last 5 years"
    startDate = new Date(today)
    startDate.setFullYear(startDate.getFullYear() - 5)
    endDate = new Date(today)
    if (endDate < minDate || startDate > maxDate) {
      // The entire range is outside the allowed daterange
      disabled.push('5years')
    }

    setDisabledPresets(disabled)

    // If the currently selected preset is now disabled, reset it
    if (selectedPreset && disabled.includes(selectedPreset)) {
      setSelectedPreset(null)
    }
  }, [daterange, dependentDriver, selectedPreset])

  // Helper function to check if current date range matches any preset
  const checkForMatchingPreset = (currentTimeframe: Timeframe): Preset => {
    if (!currentTimeframe?.from || !currentTimeframe?.to || !daterange)
      return null

    const today = new Date()
    const minDate = new Date(`${daterange.from} 00:00:00`)
    const maxDate = new Date(`${daterange.to} 23:59:00`)

    // Format date for comparison
    const formatDateForComparison = (date: Date) => {
      return date.toLocaleDateString('en-CA')
    }

    // Check "Since beginning" preset
    if (
      currentTimeframe.from === daterange.from &&
      currentTimeframe.to === daterange.to
    ) {
      return 'beginning'
    }

    // Check "Last 3 years" preset
    let startDate = new Date(today)
    startDate.setFullYear(startDate.getFullYear() - 3)
    if (startDate < minDate) startDate = minDate
    let endDate = new Date(today)
    if (endDate > maxDate) endDate = maxDate

    if (
      currentTimeframe.from === formatDateForComparison(startDate) &&
      currentTimeframe.to === formatDateForComparison(endDate)
    ) {
      return '3years'
    }

    // Check "Last 5 years" preset
    startDate = new Date(today)
    startDate.setFullYear(startDate.getFullYear() - 5)
    if (startDate < minDate) startDate = minDate
    endDate = new Date(today)
    if (endDate > maxDate) endDate = maxDate

    if (
      currentTimeframe.from === formatDateForComparison(startDate) &&
      currentTimeframe.to === formatDateForComparison(endDate)
    ) {
      return '5years'
    }

    return null
  }

  const handleDateChange = (dateType: 'from' | 'to', value: string) => {
    const formatDate = (date: Date) => {
      return date ? date.toLocaleDateString('en-CA') : ''
    }

    // Create a copy of the current timeframe
    const newTimeframe = { ...timeframe }

    // Update only the specified field (from or to)
    if (value) {
      newTimeframe[dateType] = formatDate(new Date(value))
    } else {
      delete newTimeframe[dateType]
    }

    // Only update if we have valid values
    onTimeframeChange(newTimeframe)

    // Check if the new timeframe matches any preset
    // Only do this if both from and to dates are set
    if (newTimeframe.from && newTimeframe.to) {
      const matchingPreset = checkForMatchingPreset(newTimeframe)
      setSelectedPreset(matchingPreset)
    } else {
      // If one date is missing, clear the preset selection
      setSelectedPreset(null)
    }
  }

  // Function to handle preset selection
  const handlePresetChange = (value: Preset) => {
    if (!daterange) return

    setSelectedPreset(value)

    const today = new Date()
    const minDate = new Date(`${daterange.from} 00:00:00`)
    const maxDate = new Date(`${daterange.to} 23:59:00`)

    let startDate, endDate

    // Calculate date range based on selected preset
    switch (value) {
      case 'beginning':
        startDate = minDate
        endDate = maxDate
        break

      case '3years':
        // Start date: 3 years ago from today
        startDate = new Date(today)
        startDate.setFullYear(startDate.getFullYear() - 3)
        // End date: today
        endDate = today
        break

      case '5years':
        // Start date: 5 years ago from today
        startDate = new Date(today)
        startDate.setFullYear(startDate.getFullYear() - 5)
        // End date: today
        endDate = today
        break

      default:
        return
    }

    // Ensure dates are within allowed range
    if (startDate < minDate) startDate = minDate
    if (endDate > maxDate) endDate = maxDate

    // Format dates for state update
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-CA')
    }

    // Update timeframe
    const newTimeframe = {
      from: formatDate(startDate),
      to: formatDate(endDate),
    }

    onTimeframeChange(newTimeframe)
  }

  // Effect to check for matching preset when timeframe is updated externally
  useEffect(() => {
    if (timeframe?.from && timeframe?.to) {
      const matchingPreset = checkForMatchingPreset(timeframe)
      if (matchingPreset !== selectedPreset) {
        setSelectedPreset(matchingPreset)
      }
    }
    /* eslint-disable-next-line */
  }, [timeframe?.from, timeframe?.to, daterange])

  // Helper function to check if a date is outside the allowed range
  const isDateDisabled = (date: string, type: 'from' | 'to') => {
    if (!daterange) return false

    const checkDate = new Date(date)
    const minDate = new Date(`${daterange.from} 00:00:00`)
    const maxDate = new Date(`${daterange.to} 23:59:00`)

    // For "from" field, also check against the selected "to" date if it exists
    if (type === 'from' && timeframe?.to) {
      const toDate = new Date(timeframe.to)
      return checkDate < minDate || checkDate > toDate || checkDate > maxDate
    }

    // For "to" field, also check against the selected "from" date if it exists
    if (type === 'to' && timeframe?.from) {
      const fromDate = new Date(timeframe.from)
      return checkDate < fromDate || checkDate > maxDate || checkDate < minDate
    }

    return checkDate < minDate || checkDate > maxDate
  }

  return (
    <RadioProvider
      onChange={handleModeChange}
      initValue={selectionMode}
      group="timeframe-selection"
    >
      <CombinedRowTitle>
        <span>Timeframe:</span>
        <StyledInput>
          <LabelFaded>&nbsp;</LabelFaded>
          <RadioContainer>
            <StandardRadio
              size="m"
              value="manual"
              group="timeframe-selection"
            />
          </RadioContainer>
        </StyledInput>
      </CombinedRowTitle>
      <InputsWrapper style={{ gap: '0.625rem' }}>
        <StyledInput>
          <LabelFaded>Set timeframe</LabelFaded>
          <FormDatePicker
            format="yyyy-MM-dd"
            shouldDisableDate={(date: string) => isDateDisabled(date, 'from')}
            value={timeframe?.from ? new Date(timeframe.from) : null}
            onChange={(value: string) => handleDateChange('from', value)}
            disabled={!dependentDriver || selectionMode === 'quick'}
            cleanable={false}
            style={{ width: '11rem' }}
          />
        </StyledInput>
        <DateRangeLabel>to</DateRangeLabel>
        <StyledInput>
          <LabelFaded>&nbsp;</LabelFaded>
          <FormDatePicker
            format="yyyy-MM-dd"
            shouldDisableDate={(date: string) => isDateDisabled(date, 'to')}
            value={timeframe?.to ? new Date(timeframe.to) : null}
            onChange={(value: string) => handleDateChange('to', value)}
            disabled={!dependentDriver || selectionMode === 'quick'}
            cleanable={false}
            style={{ width: '11rem' }}
          />
        </StyledInput>
        <OrDivider>or</OrDivider>
        <StyledInput>
          <LabelFaded>&nbsp;</LabelFaded>
          <RadioContainer>
            <StandardRadio size="m" value="quick" group="timeframe-selection" />
          </RadioContainer>
        </StyledInput>
        <StyledInput>
          <LabelFaded>Quick selection</LabelFaded>
          <FormSelectPicker
            data={presetOptions}
            value={selectedPreset}
            onChange={(value) => handlePresetChange(value as Preset)}
            placeholder="Custom"
            style={{ width: '15rem' }}
            disabled={!dependentDriver || selectionMode === 'manual'}
            disabledItemValues={disabledPresets}
          />
        </StyledInput>
      </InputsWrapper>
    </RadioProvider>
  )
}

export default BuildModelTimeframeSelector
